import analyticsTrigger from "@/utils/analytics/trigger/analyticsTrigger";
import amazon from "@JohnLewisPartnership/jl-design-system/dist/assets/legacy/images/carriers/amazon-shipping.svg";
import classNames from "classnames";
import Head from "next/head";
import Image from "next/image";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { trackMonetateEvent } from "@/context/MonetateContext";

import styles from "./index.module.scss";

const logoMapping = {
  "Amazon Shipping": amazon,
};

export const metadata = {
  title: `Carrier Tracking | Customer Services`,
  description: `Find all the information you need regarding delivery and collection, including tracking your order, click and collect and our delivery services.`,
};

export const getServerSideProps = async ctx => {
  const { orderNo, trackType } = ctx.query;
  const cookies = ctx.req.cookies;

  // Gate #1: Check if user is in AB-6017 experiment
  const impressionId = cookies["monetate_impression_AB6017"];

  // Gate #2: Check if user has cookie consent (Monetate ID)
  const monetateId = cookies["mt.v"];

  // Gate #3: Check if event already sent for this order+trackType
  const dedupKey = `AB6017_sent_${orderNo}_${trackType}`;
  const alreadySent = cookies[dedupKey];

  // All gates must pass, and trackType must be 'return'
  const shouldSendEvent = impressionId && monetateId && !alreadySent && orderNo && trackType === "return";

  if (shouldSendEvent) {
    // Set deduplication cookie (session cookie - expires when browser closes)
    const existingCookies = ctx.res.getHeader("Set-Cookie") || [];
    const cookiesToSet = Array.isArray(existingCookies) ? existingCookies : [existingCookies];
    cookiesToSet.push(`${dedupKey}=1; Path=/; HttpOnly; Secure; SameSite=Lax`);
    ctx.res.setHeader("Set-Cookie", cookiesToSet);

    console.log(`[AB-6017] Events will be fired on client for order ${orderNo}, trackType ${trackType}`);

    // Pass event data to client to fire events
    return {
      props: {
        shouldFireMonetateEvents: true,
      },
    };
  }

  // Log why events weren't sent (for debugging)
  const reasons = [];
  if (!impressionId) reasons.push("no impressionId");
  if (!monetateId) reasons.push("no monetateId");
  if (alreadySent) reasons.push("already sent");
  if (!orderNo) reasons.push("no orderNo");
  if (trackType !== "return") reasons.push("trackType not 'return'");

  if (reasons.length > 0) {
    console.log(`[AB-6017] Events not sent: ${reasons.join(", ")}`);
  }

  return { props: {} };
};

function CarrierTracking({ shouldFireMonetateEvents = false }) {
  const router = useRouter();
  const tntRef = useRef();
  const [headerId, setHeaderId] = useState(null);
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [isReturn, setIsReturn] = useState(false);
  const [apiLoading, setApiLoading] = useState(true);
  const { orderNo, selectedTrackingNo, trackType } = router.query;

  useEffect(() => {
    const pageInfoData = {};

    if (orderNo) pageInfoData.orderNo = orderNo;

    if (trackType) pageInfoData.trackType = trackType;

    analyticsTrigger(
      "dataStateChange",
      router.pathname,
      {
        pageInfoData,
      },
      "pageload",
    );
  }, []);

  // Fire Monetate events on client side when conditions are met
  useEffect(() => {
    console.log(`Should fire AB-6107 event: ${shouldFireMonetateEvents}`);
    if (shouldFireMonetateEvents) {
      trackMonetateEvent("AB-6017-success-return-track");
      trackMonetateEvent("AB-6017-success-aggregated");
      console.log(`[AB-6017] Client-side events fired for order ${orderNo}, trackType ${trackType}`);
    }
  }, [shouldFireMonetateEvents, orderNo, trackType]);

  const updateDeliveryInfoState = useCallback(stateParam => {
    const state = stateParam || window._prcl.store.get();
    const { apiLoading: queryLoading, query_err, checkpoints, fetchCheckpoints_failed } = state;
    setApiLoading(queryLoading);

    if (queryLoading === false && checkpoints && !query_err && !fetchCheckpoints_failed) {
      const { mappedDeliveryInfo, activeHeader } = mapDeliveryInfo(state);
      updateComponentState(mappedDeliveryInfo, activeHeader);
      handleDeliveryTypeChanges(state, checkpoints, mappedDeliveryInfo[activeHeader.id].isReturn);
    }
  }, []);

  // Helper functions
  const mapDeliveryInfo = state => {
    const mappedDeliveryInfo = {};
    state.checkpoints.header.forEach(header => {
      mappedDeliveryInfo[header.id] = {
        deliveredBy: header.courier.prettyname,
        trackingNo: header.tracking_number,
        deliveryTo: header.delivery_info?.recipient
          ? `${header.delivery_info.recipient}, ${header.delivery_info.zip_code}`
          : "",
        isReturn:
          (header.delivery_info?.isReturn || `${header.last_delivery_status?.code}`.toLowerCase() === "return") ??
          false,
      };
    });

    const activeHeader = state.checkpoints.header[state.activeTracking];
    return { mappedDeliveryInfo, activeHeader };
  };

  const updateComponentState = (mappedDeliveryInfo, activeHeader) => {
    setDeliveryInfo(mappedDeliveryInfo);
    setHeaderId(activeHeader.id);
    setIsReturn(mappedDeliveryInfo[activeHeader.id].isReturn);
  };

  const updateCheckpointsForReturn = (checkpoints, isReturn) => {
    const originalText = isReturn ? "shipping" : "return";
    const replacement = isReturn ? "return" : "shipping";
    return {
      ...checkpoints,
      header: checkpoints.header.map(h => ({
        ...h,
        actionBox: h.actionBox && {
          ...h.actionBox,
          label_communication: h.actionBox.label_communication?.replace(originalText, replacement),
        },
      })),
    };
  };

  const getUpdatedOptions = (currentOptions, isReturn) => {
    return {
      ...currentOptions,
      customTranslations: isReturn ? { en: { delivery: "Tracking number" } } : {},
    };
  };

  const handleDeliveryTypeChanges = (state, checkpoints, isReturn) => {
    const customTransaltionsDeliveryKey = state.options?.customTranslations?.en?.delivery;
    const shouldUpdate = isReturn ? !customTransaltionsDeliveryKey : customTransaltionsDeliveryKey;

    if (shouldUpdate) {
      const updatedCheckpoints = updateCheckpointsForReturn(checkpoints, isReturn);
      const updatedOptions = getUpdatedOptions(window._prcl.options, isReturn);

      window._prcl.initOpts(updatedOptions);
      window._prcl.store.set({
        options: updatedOptions,
        checkpoints: updatedCheckpoints,
      });
    }
  };

  useEffect(() => {
    if (window.ParcelLab) {
      window._prcl = new window.ParcelLab("#parcellab-track-and-trace", {
        plUserId: process.env.NEXT_PUBLIC_PL_USER_ID,
        orderNo,
        selectedTrackingNo,
        show_articleList: true,
        onRendered: updateDeliveryInfoState,
      });
      window._prcl.initialize();

      window._prcl.store.on("setActiveTracking", id => {
        setHeaderId(id);
      });
    }
  }, []);

  const TrackingHeader = ({ isReturn, apiLoading }) => {
    if (apiLoading === false) {
      return (
        <h1 className={classNames("page-heading", styles.carrierTrackerPageHeading)}>
          Track your {isReturn ? "return" : "parcel"}
        </h1>
      );
    }
  };

  return (
    <>
      <Head>
        <link rel="stylesheet" href="https://cdn.parcellab.com/css/v3/parcelLab.min.css" />
        <script src="https://cdn.parcellab.com/js/v3/parcelLab.min.js" />
      </Head>
      <div className={classNames("util-container", styles.container)}>
        <TrackingHeader deliveryInfo={deliveryInfo} headerId={headerId} isReturn={isReturn} apiLoading={apiLoading} />

        {deliveryInfo && headerId && deliveryInfo[headerId] && (
          <div className={styles.deliveryInfoContainer}>
            <div>
              <p> {isReturn ? "Returned" : "Delivered"} by</p>
              {logoMapping[deliveryInfo[headerId].deliveredBy] ? (
                <Image
                  src={logoMapping[deliveryInfo[headerId].deliveredBy]}
                  alt={deliveryInfo[headerId].deliveredBy}
                  width={186}
                  height={30}
                />
              ) : (
                <p>{deliveryInfo[headerId].deliveredBy}</p>
              )}
            </div>
            <div>
              <p> Tracking number:</p>
              <p>{deliveryInfo[headerId].trackingNo}</p>
            </div>
            {deliveryInfo[headerId].deliveryTo && (
              <div>
                <p> Delivery to</p>
                <p>{deliveryInfo[headerId].deliveryTo}</p>
              </div>
            )}
          </div>
        )}
        <div id="parcellab-track-and-trace" ref={tntRef} />
      </div>
    </>
  );
}

CarrierTracking.metadata = metadata;

export default CarrierTracking;
